import type { PresetsType } from '@assets/hdri';
import { KEY } from '@constants/key';
import { followSession } from '@lib/customization/unsavedWork';
import { LocalizationManager } from '@lib/localization';
import { handleApiResult } from '@store/error';
import { type ColorBlindnessType, isColorBlindnessType, setThemeType } from '@store/reducers/accessibilitySlice';
import { chooseRoomEnvironment } from '@store/reducers/viewerSlice';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { removeAuth, setAuth } from '../reducers/authSlice';
import { apiSlice } from './apiSlice';
import { type TeamRole } from './teamEndpoints';
import { type User } from './userEndpoints';

export interface SessionTeam {
  id: string;
  name: string;
  role: TeamRole;
  permissions: string[];
}

/** Kept on the account, so they follow the user to every device. Each is absent until picked. */
export interface UserPreferences {
  /** Interface language. */
  locale?: string;
  /** Palette status colours are drawn in. */
  colorBlindMode?: ColorBlindnessType;
  /** Backdrop of the 3D viewer. */
  roomEnvironment?: PresetsType;
  /** The team whose app settings the user's devices take; `null` drops the pick. */
  defaultTeamId?: string | null;
}

/**
 * The team whose app settings the user's devices take: `manual` when the user picked it,
 * `auto` when nothing is picked and the backend guessed it from the user's teams.
 */
export interface DefaultTeam {
  id: string;
  source: 'manual' | 'auto';
}

export interface SessionUser extends User {
  /** `false` for an account that only signs in through the identity provider. */
  hasPassword?: boolean;
  permissions: string[];
  teams: SessionTeam[];
  preferences?: UserPreferences;
  /** `null` for a user in no team. */
  defaultTeam: DefaultTeam | null;
  /**
   * For whoever defines accounts, while single sign-on is on: whether an account without a
   * password can be found by its e-mail, which is when the user form lets one be left out.
   */
  ssoAccounts?: { linkExisting: boolean };
}

interface LoginResponse {
  tokens: {
    accessToken: string;
    refreshToken: string;
  }
  user: SessionUser;
}

type RefreshTokenResponse = LoginResponse['tokens'];

interface LoginRequest {
  username: string;
  password: string;
}

interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Puts the account's preferences on screen before the session lands. Awaited ahead of
 * `setAuth` because App renders nothing until then — applying first means the first frame
 * is already in the user's language and palette, with no flash of what this browser had.
 * Each goes through the same path its Settings card does, so this browser's cache of it is
 * brought up to date too.
 *
 * A preference the account never picked leaves this browser's own choice standing, so a
 * stored value only ever overrides, never resets. A failure here must not look like a
 * failed session — the caller drops the tokens on one — so it is logged and swallowed.
 */
const applyPreferences = async (
  { preferences }: SessionUser, dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
) => {
  const { locale, colorBlindMode, roomEnvironment } = preferences ?? {};

  try {
    if (colorBlindMode && isColorBlindnessType(colorBlindMode)) dispatch(setThemeType(colorBlindMode));
    if (roomEnvironment) dispatch(chooseRoomEnvironment(roomEnvironment));

    if (locale && locale !== LocalizationManager.i18n.language) {
      await LocalizationManager.changeLanguage(locale);
    }
  } catch (error) {
    console.error('auth.applyPreferences:', error);
  }
};

/** What follows a sign-in, whichever way it was made: the server answers both with the same body. */
const completeSignIn = async (data: LoginResponse, dispatch: ThunkDispatch<unknown, unknown, UnknownAction>) => {
  localStorage.setItem(KEY.ACCESS_TOKEN, data.tokens.accessToken);
  localStorage.setItem(KEY.REFRESH_TOKEN, data.tokens.refreshToken);
  await applyPreferences(data.user, dispatch);
  // Signing in does not reload the tab: what somebody else left unsaved in an editor
  // goes here, and what this user left is theirs again.
  followSession(data.user.id);
  dispatch(setAuth(data.user));
};

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/v1/auth',
        method: 'POST',
        body: credentials,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await completeSignIn((await queryFulfilled).data, dispatch);
        } catch (_) { /* empty */ }
      },
    }),
    /**
     * The last step of a sign-in through the identity provider: the one-time code the server
     * handed this browser, with the verifier that proves it was this browser that asked.
     */
    ssoSignIn: builder.mutation<LoginResponse, { code: string; codeVerifier: string }>({
      query: (body) => ({
        url: '/v1/sso/token',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await completeSignIn((await queryFulfilled).data, dispatch);
        } catch (_) { /* empty */ }
      },
    }),
    refreshToken: builder.mutation<RefreshTokenResponse, { refreshToken: string }>({
      query: ({ refreshToken }) => ({
        url: '/v1/auth/refresh-token',
        method: 'POST',
        body: { refreshToken },
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/auth',
        method: 'DELETE',
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          localStorage.removeItem(KEY.ACCESS_TOKEN);
          localStorage.removeItem(KEY.REFRESH_TOKEN);
          // Signing out does not reload the tab either: from here on nobody is asked about
          // what this user left unsaved in an editor. It is kept in case they sign in
          // again, and goes when somebody else does.
          followSession(undefined);
          dispatch(removeAuth());
          dispatch(apiSlice.util.resetApiState());
        } catch (_) { /* empty */ }
      },
    }),
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (passwords) => ({
        url: '/v1/auth/password',
        method: 'POST',
        body: passwords,
      }),
      onQueryStarted: handleApiResult('Api_Auth_Password_Change_Successful', { handle401Error: true }),
    }),
    /**
     * Writes only the keys sent; the caller has already applied them on this device. The
     * default team is the exception: which team a pick, or dropping it, comes to is the
     * server's to say, so the session is read again once the write is through — from here
     * and not from the card that asked, which may be gone by then.
     */
    updatePreferences: builder.mutation<UserPreferences, UserPreferences>({
      query: (preferences) => ({
        url: '/v1/auth/preferences',
        method: 'PATCH',
        body: preferences,
      }),
      onQueryStarted: async (preferences, api) => {
        await handleApiResult()(preferences, api);

        if (preferences.defaultTeamId === undefined) return;

        try {
          await api.queryFulfilled;
        } catch (_) {
          return;
        }

        void api.dispatch(authApiSlice.endpoints.refreshSession.initiate(undefined, { subscribe: false, forceRefetch: true }));
      },
    }),
    getSession: builder.query<SessionUser, void>({
      query: () => '/v1/auth',
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          await applyPreferences(data, dispatch);
          followSession(data.id);
          dispatch(setAuth(data));
        } catch (_) {
          localStorage.removeItem(KEY.ACCESS_TOKEN);
          localStorage.removeItem(KEY.REFRESH_TOKEN);
          followSession(undefined);
          dispatch(removeAuth());
        }
      },
    }),
    /**
     * The session read again while one is on screen. `getSession` answers whether anyone is
     * signed in, so a read of it that fails signs the user out; here a failure leaves the
     * session as it is, since one request that did not get through says nothing about the
     * user. A token the server no longer takes is still handled by the base query.
     */
    refreshSession: builder.query<SessionUser, void>({
      query: () => '/v1/auth',
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          followSession(data.id);
          dispatch(setAuth(data));
        } catch (_) { /* empty */ }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useSsoSignInMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useUpdatePreferencesMutation,
  useGetSessionQuery,
} = authApiSlice;
